import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { GenericResModel } from 'countable@model';
import { Observable, Subscription } from 'rxjs';
import { environment } from '../../environments/environment';
import { FileExtensionModel } from '../model/file/file-extension.model';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class FileService implements OnDestroy {

  private static validExtensions: Map<string, FileExtensionModel>;
  private readonly authenticationSubject: Subscription;

  constructor(private apiService: ApiService, private authService: AuthService, private http: HttpClient) {
    this.authenticationSubject = this.authService.subject.subscribe(status => {
      if (status) {
        this.loadExtensionsFromApi();
      }
    });
  }

  private loadExtensionsFromApi() {
    if (FileService.validExtensions == null) {
      FileService.validExtensions = new Map();
      this.apiService.getAllFileExtensions().then(response => {
        response.forEach(extension => {
          FileService.validExtensions.set(extension.name, extension);
        });
      });
    }
  }

  getExtension(filename: string): FileExtensionModel {
    const parts = filename.split('.');
    const extension = parts[parts.length - 1]?.toLowerCase();

     // Check if there's an exact match
     let exactExtension = FileService.validExtensions.get(extension.toLowerCase());
     if (exactExtension) {
         return exactExtension;
     }
     if (extension?.length === 6  && extension.startsWith('20') && extension.endsWith('t2')) {
      let obj = FileService.validExtensions.get('xxxxt2');
      obj.name = extension;
      return obj;
    }
    if (extension.length === 3 && ['1', '2', '3', 't'].includes(extension[0])) {
      let obj = FileService.validExtensions.get('txx');
      obj.name = extension;
      return obj;
    }
    return null
  }

  // Use the getExtension Method as it has latest tax extension validation capabilities
  getActualExtension(filename: string): FileExtensionModel {
    return FileService.validExtensions.get(filename.toLowerCase().substring(filename.lastIndexOf('.') + 1));
  }

  /**
   * We are not supporting file extension like [NAME].tar.gz for now!
   * Use FileService::getExtension to get the extension of a raw name!
   */
  getNormalizeFileName(filename: string): string {
    return filename
      .substring(0, filename.lastIndexOf('.'))
      .trim()
      .replace(/(\W+)/gi, ' ')
      .trim()
      .replace(/\s+/g, ' ')
      .concat('.', this.getExtension(filename).name);
  }

  isValidExtension(filename: string): boolean {
    return this.getExtension(filename) != null;
  }

  isPdf(extension: FileExtensionModel): boolean {
    return extension.name === 'pdf';
  }

  /**
   * We are not supporting multiple dots in file name! We should go in that direction in future though!
   */
  getNormalizedFileNameWithoutExtension(normalizedName: string): string {
    return normalizedName.replace(/\.[^/.]+$/, "");
  }

  getFirmHeaderAndFooter(): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(environment.apiV1 + '/file/header-footer');
  }

  ngOnDestroy(): void {
    this.authenticationSubject && this.authenticationSubject.unsubscribe();
  }
}
